import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';
import clsx from 'clsx';

import {
  countMailVariables,
  mailVariableChipClassName,
  mailVariableLabels,
  splitMailVariables,
} from '../lib/mailVariables';

const VARIABLE_ATTRIBUTE = 'data-mail-variable';
const FORMAT_SHORTCUT_KEYS = ['b', 'i', 'u'];
const HISTORY_LIMIT = 100;
const TYPING_MERGE_MS = 1000;

type SelectionOffsets = { start: number; end: number };
type HistoryEntry = { value: string; caret: number };

export type MailVariableEditorHandle = {
  insert: (text: string) => void;
};

function variableOf(node: Node) {
  return node instanceof HTMLElement ? node.getAttribute(VARIABLE_ATTRIBUTE) : null;
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof HTMLElement)) return '';
  const variable = variableOf(node);
  if (variable) return variable;
  if (node.tagName === 'BR') return '';
  const inner = Array.from(node.childNodes).map(serializeNode).join('');
  const isBlock = node.tagName === 'DIV' || node.tagName === 'P';
  return isBlock && node.previousSibling ? `\n${inner}` : inner;
}

function serialize(root: Node) {
  return Array.from(root.childNodes).map(serializeNode).join('');
}

function renderValue(root: HTMLElement, value: string) {
  const fragment = document.createDocumentFragment();
  for (const part of splitMailVariables(value)) {
    if (!part) continue;
    const label = mailVariableLabels.get(part);
    if (!label) {
      fragment.append(document.createTextNode(part));
      continue;
    }
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.setAttribute(VARIABLE_ATTRIBUTE, part);
    chip.className = mailVariableChipClassName;
    chip.textContent = label;
    fragment.append(chip);
  }
  // 末尾の改行を表示させるための番兵（serialize では無視される）
  fragment.append(document.createElement('br'));
  root.replaceChildren(fragment);
}

function needsRender(root: HTMLElement, value: string) {
  const hasForeignElement = Array.from(root.childNodes).some(
    (node) => node instanceof HTMLElement && !variableOf(node) && node.tagName !== 'BR'
  );
  const chipCount = root.querySelectorAll(`[${VARIABLE_ATTRIBUTE}]`).length;
  return hasForeignElement || chipCount !== countMailVariables(value);
}

function offsetAt(root: HTMLElement, container: Node, offset: number) {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(container, offset);
  const holder = document.createElement('div');
  holder.append(range.cloneContents());
  return serialize(holder).length;
}

function readSelection(root: HTMLElement): SelectionOffsets | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  return {
    start: offsetAt(root, range.startContainer, range.startOffset),
    end: offsetAt(root, range.endContainer, range.endOffset),
  };
}

function placeCaret(root: HTMLElement, offset: number) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  let remaining = offset;
  let placed = false;
  for (const node of Array.from(root.childNodes)) {
    const length = serializeNode(node).length;
    if (node.nodeType === Node.TEXT_NODE && remaining <= length) {
      range.setStart(node, remaining);
      placed = true;
      break;
    }
    if (remaining === 0) {
      range.setStartBefore(node);
      placed = true;
      break;
    }
    remaining -= length;
    if (remaining <= 0 && variableOf(node)) {
      range.setStartAfter(node);
      placed = true;
      break;
    }
  }
  if (!placed) {
    const last = root.lastChild;
    if (last?.nodeName === 'BR') range.setStartBefore(last);
    else range.setStart(root, root.childNodes.length);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

type MailVariableEditorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
  ref?: Ref<MailVariableEditorHandle>;
};

export default function MailVariableEditor({
  value,
  onChange,
  disabled = false,
  placeholder,
  'aria-label': ariaLabel,
  className,
  ref,
}: MailVariableEditorProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const renderedValueRef = useRef<string | null>(null);
  const savedSelectionRef = useRef<SelectionOffsets | null>(null);
  const isComposingRef = useRef(false);
  const [isComposing, setIsComposing] = useState(false);
  // DOM を作り直すためブラウザ標準の undo が効かず、独自に履歴を持つ
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const lastTypingAtRef = useRef(0);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || value === renderedValueRef.current) return;
    renderedValueRef.current = value;
    renderValue(root, value);
    undoStackRef.current = [];
    redoStackRef.current = [];
  }, [value]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const root = rootRef.current;
      const offsets = root && readSelection(root);
      if (offsets) savedSelectionRef.current = offsets;
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const emit = useCallback(
    (next: string, { typing = false }: { typing?: boolean } = {}) => {
      const previous = renderedValueRef.current ?? '';
      if (next === previous) return;
      const now = Date.now();
      const mergeTyping = typing && now - lastTypingAtRef.current < TYPING_MERGE_MS;
      lastTypingAtRef.current = typing ? now : 0;
      if (!mergeTyping) {
        const caret = savedSelectionRef.current?.end ?? previous.length;
        undoStackRef.current = [...undoStackRef.current, { value: previous, caret }].slice(
          -HISTORY_LIMIT
        );
      }
      redoStackRef.current = [];
      renderedValueRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  const restoreHistory = (from: HistoryEntry[], to: HistoryEntry[]) => {
    const root = rootRef.current;
    const entry = from.pop();
    if (!root || !entry) return;
    const current = renderedValueRef.current ?? '';
    to.push({ value: current, caret: readSelection(root)?.end ?? current.length });
    lastTypingAtRef.current = 0;
    renderedValueRef.current = entry.value;
    renderValue(root, entry.value);
    placeCaret(root, entry.caret);
    savedSelectionRef.current = { start: entry.caret, end: entry.caret };
    onChange(entry.value);
  };
  const undo = () => restoreHistory(undoStackRef.current, redoStackRef.current);
  const redo = () => restoreHistory(redoStackRef.current, undoStackRef.current);
  const historyHandlersRef = useRef({ undo, redo });
  historyHandlersRef.current = { undo, redo };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // 編集メニューの「取り消す」は keydown を通らないため beforeinput で拾う
    const handleBeforeInput = (event: InputEvent) => {
      if (event.inputType !== 'historyUndo' && event.inputType !== 'historyRedo') return;
      event.preventDefault();
      if (event.inputType === 'historyUndo') historyHandlersRef.current.undo();
      else historyHandlersRef.current.redo();
    };
    root.addEventListener('beforeinput', handleBeforeInput);
    return () => root.removeEventListener('beforeinput', handleBeforeInput);
  }, []);

  const replaceSelection = useCallback(
    (text: string, offsets: SelectionOffsets | null) => {
      const root = rootRef.current;
      if (!root) return null;
      const current = serialize(root);
      const start = Math.min(offsets?.start ?? current.length, current.length);
      const end = Math.min(offsets?.end ?? current.length, current.length);
      const next = current.slice(0, start) + text + current.slice(end);
      const caret = start + text.length;
      renderValue(root, next);
      placeCaret(root, caret);
      savedSelectionRef.current = { start: caret, end: caret };
      emit(next);
      return caret;
    },
    [emit]
  );

  useImperativeHandle(
    ref,
    () => ({
      insert: (text: string) => {
        const root = rootRef.current;
        if (!root || disabled) return;
        const caret = replaceSelection(text, readSelection(root) ?? savedSelectionRef.current);
        if (caret === null) return;
        requestAnimationFrame(() => {
          root.focus();
          placeCaret(root, caret);
        });
      },
    }),
    [disabled, replaceSelection]
  );

  const syncFromDom = () => {
    const root = rootRef.current;
    if (!root) return;
    const next = serialize(root);
    if (needsRender(root, next)) {
      const caret = readSelection(root)?.end ?? next.length;
      renderValue(root, next);
      placeCaret(root, caret);
    } else if (root.lastChild?.nodeName !== 'BR') {
      root.append(document.createElement('br'));
    }
    emit(next, { typing: true });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    const key = event.key.toLowerCase();
    const isModifier = event.metaKey || event.ctrlKey;
    if (isModifier && (key === 'z' || key === 'y')) {
      event.preventDefault();
      if (key === 'y' || event.shiftKey) redo();
      else undo();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      replaceSelection('\n', readSelection(event.currentTarget));
      return;
    }
    if (isModifier && FORMAT_SHORTCUT_KEYS.includes(key)) {
      event.preventDefault();
    }
  };

  const copySelection = (event: ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;
    const holder = document.createElement('div');
    holder.append(selection.getRangeAt(0).cloneContents());
    event.preventDefault();
    event.clipboardData.setData('text/plain', serialize(holder));
    return true;
  };

  const handleCut = (event: ClipboardEvent<HTMLDivElement>) => {
    const offsets = readSelection(event.currentTarget);
    if (copySelection(event)) replaceSelection('', offsets);
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    replaceSelection(text, readSelection(event.currentTarget));
  };

  return (
    <div className={clsx('relative flex flex-col', className)}>
      {value === '' && !isComposing && placeholder && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0 p-sm text-body-sm text-sub"
        >
          {placeholder}
        </span>
      )}
      <div
        ref={rootRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        aria-placeholder={placeholder}
        aria-disabled={disabled || undefined}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={() => {
          if (!isComposingRef.current) syncFromDom();
        }}
        onCompositionStart={() => {
          isComposingRef.current = true;
          setIsComposing(true);
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          setIsComposing(false);
          syncFromDom();
        }}
        onKeyDown={handleKeyDown}
        onCopy={copySelection}
        onCut={handleCut}
        onPaste={handlePaste}
        onDrop={(event) => event.preventDefault()}
        className="min-h-0 flex-1 overflow-y-auto p-sm text-body-sm break-words whitespace-pre-wrap text-main outline-hidden"
      />
    </div>
  );
}
